import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close, downloadOutline } from 'ionicons/icons';
import { ReactElement, useCallback, useState } from 'react';
import { Attachment, AttachmentSourceType } from '../../api/graphql/generated';
import {
  ImageResolution,
  useAttachmentUrlLazy,
} from '../../api/hooks/attachment.hooks';
import { useModalAnimationFromClick } from '../../api/hooks/modal-animation.hooks';
import { trackAttachmentDownloaded } from '../../config/analytics';
import { useAttachmentViewer } from './attachment-viewer';
import './attachment-viewer.css';

export interface AttachmentsMetadata {
  currentAttachment: number;
  totalAttachments: number;
}
interface AttachmentData {
  attachment: Attachment;
  url: string | undefined;
}

interface AttachmentViewerModalProps {
  showModal: boolean;
  onDismiss: () => void;
  source: AttachmentSourceType;
  sourceId: string;
  initialAttachmentId: string | undefined;
}

export const AttachmentViewerModal = (
  props: AttachmentViewerModalProps
): ReactElement => {
  const { showModal, onDismiss, source, sourceId, initialAttachmentId } = props;
  const [currentAttachmentData, setCurrentAttachmentData] =
    useState<AttachmentData>();
  const getAttachmentUrl = useAttachmentUrlLazy();
  const [metadata, setMetadata] = useState<AttachmentsMetadata>();

  const onSelected = useCallback(
    (attachment: Attachment, meta?: AttachmentsMetadata) => {
      const url = getAttachmentUrl(attachment.path, attachment.contentType, {
        resolution: ImageResolution.HIGHRES,
      });

      setCurrentAttachmentData({
        attachment,
        url,
      });
      setMetadata(meta);
    },
    [getAttachmentUrl]
  );

  const attachmentViewer = useAttachmentViewer({
    source,
    sourceId,
    initialAttachmentId,
    onSelected,
  });

  const { enterAnimation, leaveAnimation } = useModalAnimationFromClick();

  return (
    <IonModal
      isOpen={showModal}
      className="attachment-viewer-modal"
      onDidDismiss={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDismiss();
      }}
      enterAnimation={enterAnimation}
      leaveAnimation={leaveAnimation}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot={'start'} onClick={onDismiss}>
            <IonButton>
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          {metadata && (
            <IonTitle>{`${metadata.currentAttachment} / ${metadata.totalAttachments}`}</IonTitle>
          )}
          <IonButtons slot="end">
            <IonButton
              className="attachment-viewer-file-download-button"
              shape="round"
              download={currentAttachmentData?.attachment?.name}
              href={currentAttachmentData?.url}
              onClick={(e) => {
                e.stopPropagation();
                trackAttachmentDownloaded();
              }}
              target="_blank"
              slot="end"
            >
              <IonIcon icon={downloadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent scrollY={false}>
        <div style={{ height: '100%' }}>{attachmentViewer}</div>
      </IonContent>
    </IonModal>
  );
};
